/*
 * The documents' event union and request map, hand-written beside the generated `docs.gen.ts`: the
 * only place that pairs a `doc.*` type string with its payload (protocol.md §2.5.9). tygo emits the
 * type strings as constants and the payloads as interfaces but cannot join the two; this file does,
 * `core/wire/index.ts` joins it to the aggregate unions, and `wire/fixtures.test.ts` holds it to the
 * fixtures the Go side writes for every type.
 *
 * Two shapes, as everywhere (protocol.md §2.4). The **frames** — the stream of batches, a draft's new
 * form, a caret, who is here, the head after a quiet spell — are focused-only and carry no event id:
 * they never enter the Zustand model, and `live.ts` hands each to the page that has the document
 * open. The **durable events** are ids and revs on the owner's scope, kept in the change log and
 * replayed after an absence; they too change no slice, and reach whoever listens through the same
 * router.
 */

import type { DurableEvent, EphemeralEvent } from "../../wire/events.ts"
import type * as docs from "./docs.gen.ts"

export type DocEvent =
	// Frames: focused-only, never in the change log.
	| EphemeralEvent<"doc.steps", docs.DocSteps>
	| EphemeralEvent<"doc.draft", docs.DocDraft>
	| EphemeralEvent<"doc.cursor", docs.DocCursor>
	| EphemeralEvent<"doc.viewers", docs.DocViewers>
	| EphemeralEvent<"doc.idle", docs.DocIdle>
	// Durable: thin, on the owner's scope.
	| DurableEvent<"doc.changed", docs.DocChanged>
	| DurableEvent<"doc.thread.changed", docs.DocThreadChanged>
	| DurableEvent<"doc.comment.changed", docs.DocCommentChanged>
	| DurableEvent<"doc.comment.sent_to_agent", docs.DocCommentSentToAgent>
	| DurableEvent<"doc.decision.changed", docs.DocDecisionChanged>
	| DurableEvent<"doc.version.changed", docs.DocVersionChanged>
	| DurableEvent<"doc.history.changed", docs.DocHistoryChanged>
	| DurableEvent<"doc.enum.changed", docs.DocEnumChanged>
	| DurableEvent<"doc.display.changed", docs.DocDisplayChanged>
	| DurableEvent<"doc.tabs.changed", docs.DocTabsChanged>
	| DurableEvent<"doc.link.changed", docs.DocLinkChanged>
	| DurableEvent<"doc.agents.changed", docs.DocAgentsChanged>

export type DocEventType = DocEvent["type"]

/**
 * The documents' socket requests, `[request, reply]` as `socket.send` types them: the two lanes, the
 * page a tab asks for when it sees a hole, and its caret, which is answered with nothing.
 */
export interface DocRequests {
	"doc.steps.send": [docs.DocStepsSend, docs.DocStepsAck]
	"doc.draft.send": [docs.DocDraftSend, docs.DocDraftAck]
	"doc.steps.since": [docs.DocStepsSince, docs.DocStepsSinceRes]
	"doc.cursor": [docs.DocCursorSend, Record<string, never>]
}

/**
 * The event types as a value, in the order of the Go list (`DocEventTypes`), each marked a frame or
 * durable. `satisfies` makes the object and the union agree both ways.
 */
const DOC_EVENT_KINDS = {
	"doc.steps": "frame",
	"doc.draft": "frame",
	"doc.cursor": "frame",
	"doc.viewers": "frame",
	"doc.idle": "frame",
	"doc.changed": "durable",
	"doc.thread.changed": "durable",
	"doc.comment.changed": "durable",
	"doc.comment.sent_to_agent": "durable",
	"doc.decision.changed": "durable",
	"doc.version.changed": "durable",
	"doc.history.changed": "durable",
	"doc.enum.changed": "durable",
	"doc.display.changed": "durable",
	"doc.tabs.changed": "durable",
	"doc.link.changed": "durable",
	"doc.agents.changed": "durable",
} as const satisfies Record<DocEventType, "frame" | "durable">

export const DOC_EVENT_TYPES = Object.keys(DOC_EVENT_KINDS) as readonly DocEventType[]

/** The frames: no event id, never replayed. */
export const DOC_FRAME_TYPES = DOC_EVENT_TYPES.filter((type) => DOC_EVENT_KINDS[type] === "frame")

/** The request types, in the order of the Go list (`DocRequestTypes`). */
const DOC_REQUEST_TYPE_SET = {
	"doc.steps.send": true,
	"doc.draft.send": true,
	"doc.steps.since": true,
	"doc.cursor": true,
} as const satisfies Record<keyof DocRequests, true>

export const DOC_REQUEST_TYPES = Object.keys(DOC_REQUEST_TYPE_SET) as readonly (keyof DocRequests)[]

/** Narrows an event of the aggregate union to the documents' own. */
export function isDocEvent(event: { readonly type: string }): event is DocEvent {
	return Object.prototype.hasOwnProperty.call(DOC_EVENT_KINDS, event.type)
}
